import React, {useEffect, useMemo} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {RootState, useAppDispatch} from "../../../../../redux/store";

import {
    Previsione,
    savePrevisione,
    FASE_COLLEZIONE,
    UNITA_MISURA_PREVISIONI,
} from "../previsioniSlice";
import { OrdiniLavoroList } from "./ordiniLavoro/ordiniLavoroByPrevisioneSlice";

import {ErrorStatusTypes} from "components/common/errorsDeclarations";
import {Routes_path} from "components/routerApp";
import {useForm, SubmitHandler, Controller} from "react-hook-form";

// Import custom hooks e componenti
import useAutoSave from "components/common/formUtility/useAutoSave";
import AutoSaveIndicator from "components/common/formUtility/AutoSaveIndicator";

// Material UI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import {NumericFormat} from "react-number-format";

// Selectors per anagrafiche
import {Soggetto} from "components/anagrafiche/anagraficheSlice";
import {Stagione} from "components/anagrafiche/anagraficheSlice";
import {Commerciale} from "components/anagrafiche/anagraficheSlice";
import {Tecnica} from "components/anagrafiche/anagraficheSlice";

// ============================================
// INTERFACES
// ============================================

interface PrevisioneFormProps {
    previsione: Previsione;
    consentiModifica: boolean;
    isCreating: boolean;
}

// ============================================
// COMPONENT
// ============================================

const PrevisioneForm = ({
                            previsione,
                            consentiModifica,
                            isCreating
                        }: PrevisioneFormProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // ============================================
    // REACT HOOK FORM SETUP
    // ============================================

    // Normalizza i campi numerici da string a number per evitare mismatch con NumericFormat
    const normalizeNumeric = (value: number | string | undefined | null): number | string =>
        value != null && value !== "" ? Number(value) : "";

    const {
        handleSubmit,
        register,
        formState: {errors, isDirty, isValid},
        setValue,
        watch,
        control,
        setError,
        clearErrors,
        reset,
    } = useForm<Previsione>({
        mode: 'onChange',
        defaultValues: {
            ...previsione,
            px_prod: normalizeNumeric(previsione.px_prod),
            impianto: normalizeNumeric(previsione.impianto),
            px_camp: normalizeNumeric(previsione.px_camp),
            qta_bdg: normalizeNumeric(previsione.qta_bdg),
        },
    });

    // ============================================
    // SELECTORS
    // ============================================

    const previsioniStatus = useSelector<RootState, ErrorStatusTypes>(
        (state) => state.previsioniState.previsioniList.errorsStack.status
    );

    // Ordini di lavoro associati alla previsione
    const ordiniLavoroList = useSelector<RootState, OrdiniLavoroList | undefined>(
        (state) => previsione.id
            ? state.ordiniLavoroByPrevisioneState.ordiniLavoroByPrevisione[previsione.id]
            : undefined
    );

    // Verifica se ci sono ordini di lavoro
    const hasOrdiniLavoro = (ordiniLavoroList?.results?.length || 0) > 0;

    // Opzioni fase collezione filtrate
    const faseCollezioneOptions = useMemo(() => {
        return Object.entries(FASE_COLLEZIONE)
            .map(([key, value]) => ({key, value}))
            .filter(opt => hasOrdiniLavoro ? opt.key !== 'PROT' : true);
    }, [hasOrdiniLavoro]);

    // Anagrafiche per autocomplete
    const soggetti = useSelector<RootState, Soggetto[]>(
        (state) => state.anagraficheState?.soggetti?.results || []
    );
    const mandatari = soggetti.filter(m => m.is_mandatario);
    const brands = soggetti.filter(m => m.is_brand);
    const confezionisti = soggetti.filter(m => m.is_confezionista);

    const stagioni = useSelector<RootState, Stagione[]>(
        (state) => state.anagraficheState?.stagioni?.results || []
    );

    const commerciali = useSelector<RootState, Commerciale[]>(
        (state) => state.anagraficheState?.commerciali?.results || []
    );

    const tecniche = useSelector<RootState, Tecnica[]>(
        (state) => state.anagraficheState?.tecniche?.results || []
    );

    // ============================================
    // GESTIONE ERRORI SERVER
    // ============================================

    const listaCampi = useMemo(() =>
            Object.keys(previsione) as Array<keyof Previsione>,
        [previsione]
    );

    // Reset form quando cambiano i dati
    useEffect(() => {
        reset({
            ...previsione,
            px_prod: normalizeNumeric(previsione.px_prod),
            impianto: normalizeNumeric(previsione.impianto),
            px_camp: normalizeNumeric(previsione.px_camp),
            qta_bdg: normalizeNumeric(previsione.qta_bdg),
        }, {keepDirty: false});
    }, [previsione.id, reset]);

    // Gestione errori dal server sui campi
    useEffect(() => {
        clearErrors();
        listaCampi.forEach((field) => {
            if (previsione?.errorsStack?.fieldsErrors?.[field]) {
                setError(field, {
                    type: "server",
                    message: previsione.errorsStack.fieldsErrors[field].toString(),
                });
            }
        });
    }, [previsione?.errorsStack?.fieldsErrors, listaCampi, setError, clearErrors]);

    // ============================================
    // SUBMIT HANDLER
    // ============================================

    const onSubmit: SubmitHandler<Previsione> = async (data) => {
        if (consentiModifica) {
            const result = await dispatch(savePrevisione({previsioneToSave: data}));

            // Se è creazione e salvataggio riuscito, naviga al dettaglio
            if (!previsione.id && result.meta.requestStatus === 'fulfilled') {
                const newId = (result.payload as Previsione).id;
                navigate(`${Routes_path.PREVISIONE}${newId}`);
            } else {
                reset(data); // Reset con nuovi valori per evitare isDirty
            }
        }
    };

    // ============================================
    // AUTOSAVE HOOK
    // ============================================

    const autoSave = useAutoSave({
        watch,
        handleSubmit,
        isDirty,
        isValid,
        delay: 1200,
        onSubmit,
        canSubmit: !!previsione.id, // Solo se esiste già (non in creazione)
    });

    // ============================================
    // RENDER
    // ============================================

    return (
        <Box sx={{my: 1}}>
            <form onSubmit={handleSubmit(onSubmit)}>
                {/* AutoSave Indicator - Solo se non in creazione */}
                {!isCreating && (
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
                        <AutoSaveIndicator
                            isDirty={isDirty}
                            status={previsioniStatus}
                            variant="chip"
                            size="small"
                        />
                    </Box>
                )}

                <Grid container spacing={2}>
                    {/* Stagione */}
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 8}}>
                        <Grid container spacing={2}>
                            {/* Stagione */}
                            <Grid size={{xs: 12, sm: 6, md: 4, lg: 4}}>
                                <Controller
                                    name="stagione"
                                    control={control}
                                    render={({field}) => (
                                        <Autocomplete
                                            value={
                                                (stagioni.find(s => s.id === field.value) ||
                                                (field.value != null
                                                    ? { id: field.value, descrizione: previsione.stagione_display || '' }
                                                    : null))  as Stagione | undefined
                                            }
                                            disabled={!consentiModifica}
                                            options={stagioni}
                                            getOptionLabel={(option) => option.codice || ""}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={(_, newValue) => {
                                                clearErrors();
                                                setValue("stagione", newValue?.id || undefined, {
                                                    shouldDirty: true,
                                                    shouldValidate: true
                                                });
                                                setValue("stagione_display", newValue?.descrizione || "", {
                                                    shouldDirty: true,
                                                });

                                                if (previsione.id) {
                                                    handleSubmit(onSubmit)();
                                                }
                                            }}
                                            disableClearable
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Stagione"
                                                    required
                                                    error={!!errors?.stagione}
                                                    helperText={errors?.stagione?.message}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                            {/* Fase Collezione */}
                            <Grid size={{xs: 12, sm: 6, md: 4, lg: 4}}>
                                <Controller
                                    name="fase_collezione"
                                    control={control}
                                    defaultValue="PROT"
                                    render={({field}) => (
                                        <Autocomplete
                                            value={faseCollezioneOptions.find(opt => opt.key === field.value)}
                                            disableClearable
                                            disabled={!consentiModifica}
                                            options={faseCollezioneOptions}
                                            getOptionLabel={(option) => option?.value || ""}
                                            isOptionEqualToValue={(option, value) => option?.key === value?.key}
                                            onChange={(_, newValue) => {
                                                const selectedKey = newValue?.key || "PROT";
                                                field.onChange(selectedKey);

                                                if (previsione.id) {
                                                    handleSubmit(onSubmit)();
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Fase Collezione"
                                                    required
                                                    error={!!errors?.fase_collezione}
                                                    helperText={
                                                        errors?.fase_collezione?.message ||
                                                        (hasOrdiniLavoro ? "Non è possibile tornare a Prototipo con ordini di lavoro presenti" : "")
                                                    }
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                            {/* Commerciale */}
                            <Grid size={{xs: 12, sm: 6, md: 4, lg: 4}}>
                                <Controller
                                    name="commerciale"
                                    control={control}
                                    render={({field}) => (
                                        <Autocomplete
                                            value={
                                                (commerciali.find(c => c.id === field.value) ||
                                                    (field.value != null
                                                        ? { id: field.value, user_display: previsione.commerciale_display || '' }
                                                        : null)) as Commerciale | undefined
                                            }
                                            disableClearable
                                            disabled={!consentiModifica}
                                            options={commerciali}
                                            // disableClearable
                                            getOptionLabel={(option) => option.user_display || ""}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={(_, newValue) => {
                                                clearErrors();

                                                setValue("commerciale", newValue?.id || undefined, {
                                                    shouldDirty: true,
                                                    shouldValidate: true
                                                });
                                                setValue("commerciale_display", newValue?.user_display || "", {
                                                    shouldDirty: true,
                                                });

                                                if (previsione.id) {
                                                    handleSubmit(onSubmit)();
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Commerciale"
                                                    required
                                                    error={!!errors?.commerciale}
                                                    helperText={errors?.commerciale?.message}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                        <Divider sx={{my: 2}}/>
                        <Grid container spacing={2}>
                            {/* Mandatario */}
                            <Grid size={{xs: 12, sm: 6, md: 4, lg: 4}}>
                                <Controller
                                    name="mandatario"
                                    control={control}
                                    render={({field}) => (
                                        <Autocomplete
                                            // value={
                                            //     mandatari.find(m => m.id === field.value) ||
                                            //     (field.value != undefined
                                            //         ? { id: field.value, nome: previsione.mandatario_display || '' } as Soggetto
                                            //         : undefined)
                                            // }
                                            value={
                                                (mandatari.find(m => m.id === field.value) ||
                                                    (field.value != null
                                                        ? { id: field.value, user_display: previsione.mandatario_display || '' }
                                                        : null)) as Soggetto | undefined
                                            }
                                            disableClearable
                                            disabled={!consentiModifica}
                                            options={mandatari}
                                            getOptionLabel={(option) => option.nome || ""}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={(_, newValue) => {
                                                clearErrors();
                                                setValue("mandatario", newValue?.id || undefined, {
                                                    shouldDirty: true,
                                                    shouldValidate: true
                                                });
                                                setValue("mandatario_display", newValue?.nome || "", {
                                                    shouldDirty: true,
                                                });

                                                if (previsione.id) {
                                                    handleSubmit(onSubmit)();
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    required
                                                    label="Mandatario"
                                                    error={!!errors?.mandatario}
                                                    helperText={errors?.mandatario?.message}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Brand */}
                            <Grid size={{xs: 12, sm: 6, md: 4, lg: 4}}>
                                <Controller
                                    name="brand"
                                    control={control}
                                    render={({field}) => (
                                        <Autocomplete
                                            value={brands.find(b => b.id === field.value) ||
                                                (field.value != null
                                                    ? { id: field.value, nome: previsione.brand_display || '' } as Soggetto
                                                    : null)}
                                            disabled={!consentiModifica}
                                            options={brands}
                                            getOptionLabel={(option) => option.nome || ""}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={(_, newValue) => {
                                                clearErrors();
                                                setValue("brand", newValue?.id || undefined, {
                                                    shouldDirty: true,
                                                    shouldValidate: true
                                                });
                                                setValue("brand_display", newValue?.nome || "", {
                                                    shouldDirty: true,
                                                });

                                                if (previsione.id) {
                                                    handleSubmit(onSubmit)();
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Brand"
                                                    error={!!errors?.brand}
                                                    helperText={errors?.brand?.message}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Confezionista */}
                            <Grid size={{xs: 12, sm: 6, md: 4, lg: 4}}>
                                <Controller
                                    name="confezionista"
                                    control={control}
                                    render={({field}) => (
                                        <Autocomplete
                                            value={confezionisti.find(c => c.id === field.value) ||
                                                (field.value != null
                                                    ? { id: field.value, nome: previsione.confezionista_display || '' } as Soggetto
                                                    : null)}
                                            disabled={!consentiModifica}
                                            options={confezionisti}
                                            getOptionLabel={(option) => option.nome || ""}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={(_, newValue) => {
                                                clearErrors();
                                                setValue("confezionista", newValue?.id || undefined, {
                                                    shouldDirty: true,
                                                    shouldValidate: true
                                                });
                                                setValue("confezionista_display", newValue?.nome || "", {
                                                    shouldDirty: true,
                                                });

                                                if (previsione.id) {
                                                    handleSubmit(onSubmit)();
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Confezionista"
                                                    error={!!errors?.confezionista}
                                                    helperText={errors?.confezionista?.message}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 4}}>
                        <TextField
                            label="Note"
                            variant="outlined"
                            slotProps={{
                                inputLabel: {shrink: true},
                            }}
                            multiline
                            rows={5}
                            {...register("note")}
                            disabled={!consentiModifica}
                            error={!!errors?.note}
                            helperText={errors?.note?.message}
                            fullWidth
                        />
                    </Grid>
                </Grid>

                {/* SEZIONE SOGGETTI */}


                {/* SEZIONE IDENTIFICAZIONE */}
                <Divider sx={{my: 2}} textAlign="left"><Typography variant='h5'>Identificazione</Typography></Divider>
                <Grid container spacing={2}>
                    {/* Codice Item */}
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 3}}>
                        <TextField
                            label="Codice Item"
                            required
                            disabled={!consentiModifica}
                            variant="outlined"
                            slotProps={{
                                inputLabel: {shrink: true},
                            }}
                            {...register("codice_item", {
                                required: "Il codice item è obbligatorio",
                                maxLength: {value: 25, message: "Max 25 caratteri"}
                            })}
                            error={!!errors?.codice_item}
                            helperText={errors?.codice_item?.message}
                            autoComplete="off"
                            fullWidth
                        />
                    </Grid>


                    {/* Articolo Cliente */}
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 3}}>
                        <TextField
                            label="Articolo Cliente"
                            disabled={!consentiModifica}
                            variant="outlined"
                            slotProps={{
                                inputLabel: {shrink: true},
                            }}
                            {...register("articolo_cliente", {
                                maxLength: {value: 50, message: "Max 50 caratteri"}
                            })}
                            error={!!errors?.articolo_cliente}
                            helperText={errors?.articolo_cliente?.message}
                            fullWidth
                        />
                    </Grid>

                    {/* Collezione */}
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 3}}>
                        <TextField
                            label="Collezione"
                            disabled={!consentiModifica}
                            variant="outlined"
                            slotProps={{
                                inputLabel: {shrink: true},
                            }}
                            {...register("collezione", {
                                maxLength: {value: 50, message: "Max 50 caratteri"}
                            })}
                            error={!!errors?.collezione}
                            helperText={errors?.collezione?.message}
                            fullWidth
                        />
                    </Grid>


                    {/* Tecnica */}
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 3}}>
                        <Controller
                            name="tecnica"
                            control={control}
                            render={({field}) => (
                                <Autocomplete
                                    value={tecniche.find(t => t.id === field.value) ||
                                        (field.value != null
                                            ? { id: field.value, nome: previsione.tecnica_display || '' } as Tecnica
                                            : null)}
                                    disabled={!consentiModifica}
                                    options={tecniche}
                                    getOptionLabel={(option) => option.nome || ""}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={(_, newValue) => {
                                        clearErrors();
                                        setValue("tecnica", newValue?.id || undefined, {
                                            shouldDirty: true,
                                            shouldValidate: true
                                        });
                                        setValue("tecnica_display", newValue?.nome || "", {
                                            shouldDirty: true,
                                        });

                                        if (previsione.id) {
                                            handleSubmit(onSubmit)();
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Tecnica"
                                            error={!!errors?.tecnica}
                                            helperText={errors?.tecnica?.message}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>

                    {/* Descrizione Item */}
                    <Grid size={{xs: 12, sm: 12, md: 8, lg: 9}}>
                        <TextField
                            label="Descrizione Item"
                            disabled={!consentiModifica}
                            variant="outlined"
                            slotProps={{
                                inputLabel: {shrink: true},
                            }}
                            {...register("descrizione_item", {
                                maxLength: {value: 100, message: "Max 100 caratteri"}
                            })}
                            error={!!errors?.descrizione_item}
                            helperText={errors?.descrizione_item?.message}
                            fullWidth
                        />
                    </Grid>

                    {/* Unità Misura */}
                    <Grid size={{xs: 12, sm: 6, md: 4, lg: 3}}>
                        <Controller
                            name="unita_misura"
                            control={control}
                            defaultValue="PZ"
                            render={({field}) => (
                                <Autocomplete
                                    value={Object.entries(UNITA_MISURA_PREVISIONI)
                                                 .map(([key, value]) => ({key, value}))
                                                 .find(opt => opt.key === field.value) || null}
                                    disabled={!consentiModifica}
                                    options={Object.entries(UNITA_MISURA_PREVISIONI)
                                                   .map(([key, value]) => ({key, value}))}
                                    getOptionLabel={(option) => option?.value || ""}
                                    isOptionEqualToValue={(option, value) => option?.key === value?.key}
                                    onChange={(_, newValue) => {
                                        const selectedKey = newValue?.key || "PZ";
                                        field.onChange(selectedKey);

                                        if (previsione.id) {
                                            handleSubmit(onSubmit)();
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="U.M."
                                            error={!!errors?.unita_misura}
                                            helperText={errors?.unita_misura?.message}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Divider sx={{my: 2}} textAlign="left"><Typography variant='h5'>Prezzi</Typography></Divider>

                <Grid container spacing={2}>
                    <Grid size={{xs: 12, sm: 6, md: 3}}>
                        <Controller
                            name="px_prod"
                            control={control}
                            render={({field}) => (
                                <NumericFormat
                                    customInput={TextField}
                                    label="Prezzo Produzione"
                                    disabled={!consentiModifica}
                                    variant="outlined"
                                    slotProps={{
                                        inputLabel: {shrink: true},
                                    }}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix="€ "
                                    value={field.value || ""}
                                    onValueChange={(values) => {
                                        field.onChange(values.floatValue || "");
                                    }}
                                    error={!!errors?.px_prod}
                                    helperText={errors?.px_prod?.message || "Prezzo di produzione"}
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>

                    {/* Impianto */}
                    <Grid size={{xs: 12, sm: 6, md: 3}}>
                        <Controller
                            name="impianto"
                            control={control}
                            render={({field}) => (
                                <NumericFormat
                                    customInput={TextField}
                                    label="Impianto"
                                    disabled={!consentiModifica}
                                    variant="outlined"
                                    slotProps={{
                                        inputLabel: {shrink: true},
                                    }}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix="€ "
                                    value={field.value || ""}
                                    onValueChange={(values) => {
                                        field.onChange(values.floatValue || "");
                                    }}
                                    error={!!errors?.impianto}
                                    helperText={errors?.impianto?.message || "Costo impianto"}
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>

                    {/* Prezzo Campione */}
                    <Grid size={{xs: 12, sm: 6, md: 3}}>
                        <Controller
                            name="px_camp"
                            control={control}
                            render={({field}) => (
                                <NumericFormat
                                    customInput={TextField}
                                    label="Prezzo Campione"
                                    disabled={!consentiModifica}
                                    variant="outlined"
                                    slotProps={{
                                        inputLabel: {shrink: true},
                                    }}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix="€ "
                                    value={field.value || ""}
                                    onValueChange={(values) => {
                                        field.onChange(values.floatValue || "");
                                    }}
                                    error={!!errors?.px_camp}
                                    helperText={errors?.px_camp?.message || "Prezzo del campione"}
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>

                    {/* Quantità BDG */}
                    {previsione.fase_collezione === 'CAMP' && (
                    <Grid size={{xs: 12, sm: 6, md: 3}}>
                        <Controller
                            name="qta_bdg"
                            control={control}
                            render={({field}) => (
                                <NumericFormat
                                    customInput={TextField}
                                    label="Quantità BDG"
                                    disabled={!consentiModifica}
                                    variant="outlined"
                                    slotProps={{
                                        inputLabel: {shrink: true},
                                    }}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    decimalScale={1}
                                    fixedDecimalScale
                                    value={field.value || ""}
                                    onValueChange={(values) => {
                                        field.onChange(values.floatValue || "");
                                    }}
                                    error={!!errors?.qta_bdg}
                                    helperText={errors?.qta_bdg?.message || "Quantità budget"}
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>
                    )}


                    {/* Pulsanti solo per creazione */}
                    {isCreating && (
                        <Grid size={{xs: 12}}>
                            <Stack direction="row" spacing={2} sx={{mt: 3}} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate(Routes_path.PREVISIONI)}
                                >
                                    Annulla
                                </Button>
                                <Button
                                    disabled={!isDirty || !isValid}
                                    variant="contained"
                                    type="submit"
                                >
                                    Salva Listino
                                </Button>
                            </Stack>
                        </Grid>
                    )}
                </Grid>
            </form>
        </Box>
    );
};

export default PrevisioneForm;